import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { submitNewsletterSignup, getSubscriberCount } from '../api/newsletter';
import { syncUserProfileDebounced } from '../api/users';
import { trackNewsletterSignupAttempt } from '../analytics';
import { setCategoryConsent } from '../utils/consentPreferences';
import { useGettoncini } from '../contexts/GettonciniContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import { hasNewsletterSignupCompleted } from '../util';
import {
  resolveNewsletterSignupAlert,
  NEWSLETTER_TRANSACTION_KEY,
} from '../utils/newsletterGettoncini';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import styles from '../styles/NewsletterLandingPage.module.css';
import FaIcon from './FaIcon';
import GettonciniCostChip from './GettonciniCostChip';

const BENEFITS = [
  {
    emoji: '🎮',
    title: 'Nuovi giochini in arrivo',
    description: 'Sii il primo a scoprire i nuovi giochi che lanciamo. Anteprime esclusive e accesso anticipato per i nostri iscritti.'
  },
  {
    emoji: '💡',
    title: 'Aiutini esclusivi',
    description: 'Suggerimenti e trucchi per migliorare le tue performance. Sblocca le partite più difficili con i nostri consigli.'
  },
  {
    emoji: '🔥',
    title: 'Non perdere la tua serie',
    description: 'Promemoria utili a mantenere la tua streak. Una piccola notifica può fare una grande differenza.'
  },
  {
    emoji: '🏆',
    title: 'Sfide settimanali',
    description: 'Partecipa a sfide speciali ed competizioni esclusive riservate alla nostra community di iscritti.'
  },
  {
    emoji: '🎁',
    title: 'Offerte speciali',
    description: 'Promozioni riservate, gettoncini bonus e sorprese per chi è un lettore della nostra newsletter.'
  },
  {
    emoji: '📚',
    title: 'Contenuti per imparare',
    description: 'Approfondimenti sulla lingua italiana, curiosità linguistiche e pillole di apprendimento stimolanti.'
  }
];

const NewsletterLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { updateBalance, transactionTypes } = useGettoncini();
  const { queueAlert } = useAlertQueueActions();
  const { 
    isNewsletterSubscribed: isSubscribedFromContext, 
    email: profileEmail,
    isLoading: profileLoading,
    refreshProfile 
  } = useUserProfile();
  
  // Form state for hero
  const [heroEmail, setHeroEmail] = useState('');
  const [heroSubmitting, setHeroSubmitting] = useState(false);
  const [heroStatus, setHeroStatus] = useState(null);
  
  // Form state for bottom CTA
  const [ctaEmail, setCtaEmail] = useState('');
  const [ctaSubmitting, setCtaSubmitting] = useState(false);
  const [ctaStatus, setCtaStatus] = useState(null);
  
  // Local subscription state (for immediate feedback after signup)
  const [localSubscribed, setLocalSubscribed] = useState(false);
  
  // Subscriber count state
  const [subscriberCount, setSubscriberCount] = useState(null);
  const newsletterRewardAmount = useMemo(() => {
    const configuredAmount = Number(transactionTypes?.[NEWSLETTER_TRANSACTION_KEY]?.amount);
    if (Number.isFinite(configuredAmount) && configuredAmount > 0) {
      return Math.trunc(configuredAmount);
    }
    return null;
  }, [transactionTypes]);

  // Fetch subscriber count on mount
  useEffect(() => {
    getSubscriberCount()
      .then(count => setSubscriberCount(count))
      .catch(() => setSubscriberCount(null));
  }, []);

  // Get next param for redirects
  const nextParam = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const nxt = params.get('next');
      if (nxt && typeof nxt === 'string' && nxt.startsWith('/') && !nxt.startsWith('//')) return nxt;
    } catch (_) {}
    return null;
  }, [location.search]);

  // Determine subscription status from context (for signed-in users) or localStorage (for anonymous)
  const isSubscribed = isSignedIn 
    ? (isSubscribedFromContext || localSubscribed)
    : (hasNewsletterSignupCompleted() || localSubscribed);

  // Redirect if already subscribed and we have a next param
  useEffect(() => {
    if (isSubscribed && nextParam && !profileLoading) {
      navigate(nextParam, { replace: true });
    }
  }, [isSubscribed, nextParam, navigate, profileLoading]);

  // Get best available email: profile > Clerk > localStorage
  const bestEmail = useMemo(() => {
    // Prefer profile email from context (authoritative for signed-in users)
    if (profileEmail) return profileEmail;
    
    // Fall back to Clerk email
    try {
      if (isSignedIn && user) {
        if (user.primaryEmailAddress?.emailAddress) return user.primaryEmailAddress.emailAddress;
        const first = user.emailAddresses?.[0]?.emailAddress;
        if (first) return first;
      }
    } catch (_) {}
    
    // Fall back to localStorage
    return localStorage.getItem('email') || '';
  }, [profileEmail, isSignedIn, user]);

  // Initialize emails with best available email
  useEffect(() => {
    if (bestEmail) {
      setHeroEmail(bestEmail);
      setCtaEmail(bestEmail);
    }
  }, [bestEmail]);

  // Generic submit handler
  const handleSubmit = async (email, setSubmitting, setStatus, formId) => {
    if (!email.trim()) {
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      const userId = localStorage.getItem('user-id');
      if (!userId) {
        throw new Error('No user ID found');
      }

      const token = isSignedIn
        ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
        : null;

      const sourceSurface = formId === 'hero'
        ? 'newsletter_landing_hero_form'
        : 'newsletter_landing_cta_form';
      const result = await submitNewsletterSignup(email.trim(), token, sourceSurface);

      if (result.success) {
        if (email.trim() !== localStorage.getItem('email')) {
          localStorage.setItem('email', email.trim());
        }
        setCategoryConsent({ marketing: true, source: 'settings' });
        localStorage.setItem('newsletter-signup-completed', Date.now().toString());
        syncUserProfileDebounced();

        const alertPayload = resolveNewsletterSignupAlert({
          result,
          isSignedIn,
          transactionTypes,
        });
        if (alertPayload) {
          const balance = alertPayload?.payload?.gettoncini?.balance;
          if (typeof balance === 'number') {
            updateBalance(balance);
          }
          queueResourceAlertsFromPayload({
            queueAlert,
            payload: alertPayload.payload,
            isAnonymous: alertPayload.isAnonymous,
            firstAlertDelayMs: 1000,
            signedInDurationMs: 8000,
            anonymousDurationMs: 8000,
          });
        }
        
        // Refresh profile context to get updated subscription status
        if (isSignedIn) {
          refreshProfile().catch(() => {});
        }
        
        // Set local subscribed state for immediate UI feedback
        setLocalSubscribed(true);
        
        trackNewsletterSignupAttempt(true);
        setStatus('success');
        
        // Sync both forms
        setHeroEmail(email.trim());
        setCtaEmail(email.trim());
        setHeroStatus('success');
        setCtaStatus('success');
      } else {
        trackNewsletterSignupAttempt(false);
        setStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      trackNewsletterSignupAttempt(false);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  // Determine subscribed email (prefer what we have available)
  const subscribedEmail = bestEmail;
  
  const isAnySuccess = heroStatus === 'success' || ctaStatus === 'success';
  const showSubscribedState = isSubscribed || isAnySuccess;

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Background decorations */}
        <div className={styles.heroBackground}>
          <FaIcon className={`fas fa-envelope ${styles.floatingShape} ${styles.shape1}`}  />
          <FaIcon className={`fas fa-paper-plane ${styles.floatingShape} ${styles.shape2}`}  />
          <FaIcon className={`fas fa-envelope-open-text ${styles.floatingShape} ${styles.shape3}`}  />
          <FaIcon className={`fas fa-bell ${styles.floatingShape} ${styles.shape4}`}  />
          <FaIcon className={`fas fa-star ${styles.floatingShape} ${styles.shape5}`}  />
        </div>
        
        <div className={styles.sparkles}>
          <div className={styles.sparkle} />
          <div className={styles.sparkle} />
          <div className={styles.sparkle} />
          <div className={styles.sparkle} />
          <div className={styles.sparkle} />
          <div className={styles.sparkle} />
        </div>

        <div className={styles.heroContent}>
          <div className={styles.iconContainer}>
            <div className={styles.newsletterIcon}>
              <FaIcon className="fas fa-envelope-open-text"  />
            </div>
          </div>

          <h1 className={styles.heroTitle}>
            Non perderti niente!
          </h1>
          
          <p className={styles.heroSubtitle}>
            Iscriviti alla newsletter per scoprire i giochini extra e ricevere aggiornamenti in anteprima e offerte speciali riservate alla nostra community.
          </p>

          {showSubscribedState ? (
            <div className={styles.subscribedContainer}>
              <div className={styles.subscribedBadge}>
                <FaIcon className={`fas fa-check-circle ${styles.subscribedIcon}`}  />
                <div className={styles.subscribedText}>
                  <span className={styles.subscribedLabel}>Sei già iscritto!</span>
                  {(heroEmail || subscribedEmail) && (
                    <span className={styles.subscribedEmail}>{heroEmail || subscribedEmail}</span>
                  )}
                </div>
              </div>
              <Link to="/" className={styles.homeButton}>
                <FaIcon className="fas fa-gamepad"  />
                <span>Vai ai giochi</span>
              </Link>
            </div>
          ) : (
            <form 
              className={styles.heroForm}
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(heroEmail, setHeroSubmitting, setHeroStatus, 'hero');
              }}
            >
              <div className={styles.inputGroup}>
                <input
                  type="email"
                  value={heroEmail}
                  onChange={(e) => {
                    setHeroEmail(e.target.value);
                    setCtaEmail(e.target.value);
                    setHeroStatus(null);
                  }}
                  placeholder="La tua email"
                  className={styles.emailInput}
                  disabled={heroSubmitting}
                  required
                />
                <div className={styles.submitButtonWrap}>
                  <button
                    type="submit"
                    className={`${styles.submitButton} ${heroSubmitting ? styles.submitting : ''}`}
                    disabled={heroSubmitting || !heroEmail.trim()}
                  >
                    {heroSubmitting ? 'Iscrizione...' : 'Iscriviti gratis'}
                  </button>
                  <GettonciniCostChip
                    amount={newsletterRewardAmount}
                    direction="earn"
                    compact
                    className={styles.submitRewardChip}
                  />
                </div>
              </div>
              
              {heroStatus === 'error' && (
                <div className={styles.errorMessage}>
                  Si è verificato un errore. Riprova più tardi.
                </div>
              )}
              
              <p className={styles.privacyNote}>
                Puoi disiscriverti in qualsiasi momento. Niente spam, promesso!
              </p>
            </form>
          )}
        </div>

        {!showSubscribedState && (
          <div className={styles.scrollIndicator}>
            <span>Scopri i vantaggi</span>
            <FaIcon className={`fas fa-chevron-down ${styles.scrollArrow}`}  />
          </div>
        )}
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Cosa riceverai?</h2>
          <p className={styles.sectionSubtitle}>
            Entra a far parte della nostra community e scopri tutti i vantaggi 
            riservati agli iscritti alla newsletter.
          </p>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <span className={styles.benefitEmoji}>{benefit.emoji}</span>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className={styles.socialProofSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Una community in crescita</h2>
          <p className={styles.sectionSubtitle}>
            {subscriberCount
              ? `Unisciti a ${subscriberCount.toLocaleString('it-IT')} altri giocatori appassionati della lingua italiana.`
              : 'Unisciti a migliaia di altri giocatori appassionati della lingua italiana.'}
          </p>
          
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>6</span>
              <span className={styles.statLabel}>Giochi disponibili</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>∞</span>
              <span className={styles.statLabel}>Nuove sfide ogni giorno</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>100%</span>
              <span className={styles.statLabel}>Gratis</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          {showSubscribedState ? (
            <>
              <h2 className={styles.ctaTitle}>Grazie per essere con noi! 🎉</h2>
              <p className={styles.ctaSubtitle}>
                Riceverai presto aggiornamenti esclusivi nella tua casella di posta.
              </p>
              <div className={styles.subscribedContainer}>
                <div className={styles.subscribedBadge}>
                  <FaIcon className={`fas fa-check-circle ${styles.subscribedIcon}`}  />
                  <div className={styles.subscribedText}>
                    <span className={styles.subscribedLabel}>Iscritto</span>
                    {(ctaEmail || subscribedEmail) && (
                      <span className={styles.subscribedEmail}>{ctaEmail || subscribedEmail}</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className={styles.ctaTitle}>Vuoi rimanere sempre aggiornato?</h2>
              <p className={styles.ctaSubtitle}>
                {newsletterRewardAmount
                  ? `Iscriviti ora e ${isSignedIn ? 'ricevi' : 'riscatta con un account'} ${newsletterRewardAmount.toLocaleString('it-IT')} gettoncini bonus!`
                  : 'Iscriviti per ricevere i nostri aggiornamenti!'}
              </p>

              <form 
                className={styles.heroForm}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(ctaEmail, setCtaSubmitting, setCtaStatus, 'cta');
                }}
              >
                <div className={styles.inputGroup}>
                  <input
                    type="email"
                    value={ctaEmail}
                    onChange={(e) => {
                      setCtaEmail(e.target.value);
                      setHeroEmail(e.target.value);
                      setCtaStatus(null);
                    }}
                    placeholder="La tua email"
                    className={styles.emailInput}
                    disabled={ctaSubmitting}
                    required
                  />
                  <div className={styles.submitButtonWrap}>
                    <button
                      type="submit"
                      className={`${styles.submitButton} ${ctaSubmitting ? styles.submitting : ''}`}
                      disabled={ctaSubmitting || !ctaEmail.trim()}
                    >
                      {ctaSubmitting ? 'Iscrizione...' : 'Iscriviti ora'}
                    </button>
                    <GettonciniCostChip
                      amount={newsletterRewardAmount}
                      direction="earn"
                      compact
                      className={styles.submitRewardChip}
                    />
                  </div>
                </div>
                
                {ctaStatus === 'error' && (
                  <div className={styles.errorMessage}>
                    Si è verificato un errore. Riprova più tardi.
                  </div>
                )}
                
                <p className={styles.privacyNote}>
                  Puoi disiscriverti in qualsiasi momento.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Footer Note */}
      <section className={styles.footerNote}>
        <p className={styles.footerText}>
          Hai già un account? <Link to="/" className={styles.footerLink}>Torna ai giochi</Link>
        </p>
      </section>
    </PageShell>
  );
};

export default NewsletterLandingPage;
